import type { Tagged } from 'type-fest';
import { Const } from '../../../shared';
import { dayjs } from '../../../shared/utils/date';
import { normalizeEmail } from '../../../shared/utils/email';
import { MutableEntity } from '../common';
import type { Group } from '../group';
import type { Organization } from '../organization';
import type { Project } from '../project';
import { UniqueId } from '../UniqueId';
import { Grant as _Grant } from './grant';
import { _OrganizationMembership, _ProjectMembership } from './membership';

// 1.3億回生成したときの衝突確率が1%
// こちらのサイトの計算で計算 => https://zelark.github.io/nano-id-cc/
const uuidGenerator = UniqueId.GetFactory(10);

export type Account = MutableEntity & {
  id: Account.Id;
  idaasId: Account.IdaasId;
  type: Account.Type;
  name: string;
  email: string;
  profileImageSrc: string;
  isSystemAdmin: boolean; // システム管理者 かどうか
  verificationEmailSentAt: dayjs | undefined;
  authSessionInvalidBefore?: dayjs | undefined;
  firebaseDisabled?: Account.FirebaseDisabled | undefined;
  memberships: Account.Membership.Organization[];
};

export namespace Account {
  export type Type = 'user' | 'service';

  export type Id = Tagged<UniqueId, 'accountId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export const FromString = (id: string): Id => uuidGenerator.FromString(id) as Id;
  }

  export type IdaasId = {
    firebase?: IdaasId.Firebase;
  };
  export namespace IdaasId {
    export type Firebase = Tagged<string, 'firebaseId'>;
    export type Props = {
      firebase?: string | null;
    };
    export const Construct = (props: Props): IdaasId => {
      if (!props.firebase) return {};
      return {
        firebase: props.firebase as Firebase,
      };
    };
  }

  export const FirebaseDisabledReason = {
    ManagedAccountDeactivation: 'managed_account_deactivation',
    LoginAttemptLockout: 'login_attempt_lockout',
  } as const;
  export type FirebaseDisabledReason = (typeof FirebaseDisabledReason)[keyof typeof FirebaseDisabledReason];

  export type FirebaseDisabled = {
    reason: FirebaseDisabledReason;
    disabledAt: dayjs;
    disabledById?: Id | undefined;
  };
  export namespace FirebaseDisabled {
    export type Props = {
      reason?: string | null | undefined;
      disabledAt?: string | Date | dayjs | null | undefined;
      disabledById?: string | null | undefined;
    };

    const isReason = (reason: string): reason is FirebaseDisabledReason => {
      return Object.values(FirebaseDisabledReason).includes(reason as FirebaseDisabledReason);
    };

    export const Construct = (props: Props): FirebaseDisabled | undefined => {
      if (!props.reason && !props.disabledAt && !props.disabledById) return undefined;
      if (!props.reason || !props.disabledAt) throw new Error('Firebase disabled state requires reason and disabledAt');
      if (!isReason(props.reason)) throw new Error(`Unknown Firebase disabled reason: ${props.reason}`);
      return {
        reason: props.reason,
        disabledAt: dayjs(props.disabledAt),
        disabledById: props.disabledById ? Id.FromString(props.disabledById) : undefined,
      };
    };
  }

  export namespace Membership {
    export import Organization = _OrganizationMembership;
    export import Project = _ProjectMembership;
  }
  export import Grant = _Grant;

  export type Props = MutableEntity.Props & {
    id: string;
    idaasId: IdaasId.Props;
    type?: Type | undefined;
    name: string;
    email: string;
    profileImageSrc: string;
    isSystemAdmin: boolean;
    verificationEmailSentAt?: string | Date | dayjs | undefined;
    authSessionInvalidBefore?: string | Date | dayjs | undefined;
    firebaseDisabled?: FirebaseDisabled.Props | undefined;
    memberships: Membership.Organization.Props[];
  };

  export const Construct = (props: Props): Account => {
    const firebaseDisabled = FirebaseDisabled.Construct(props.firebaseDisabled ?? {});
    return {
      ...MutableEntity.Construct(props),
      id: Account.Id.FromString(props.id),
      idaasId: Account.IdaasId.Construct(props.idaasId),
      type: props.type ?? 'user',
      name: props.name,
      email: props.email,
      profileImageSrc: props.profileImageSrc,
      isSystemAdmin: props.isSystemAdmin,
      verificationEmailSentAt: props.verificationEmailSentAt ? dayjs(props.verificationEmailSentAt) : undefined,
      ...(props.authSessionInvalidBefore ? { authSessionInvalidBefore: dayjs(props.authSessionInvalidBefore) } : {}),
      ...(firebaseDisabled ? { firebaseDisabled } : {}),
      memberships: props.memberships.map(Membership.Organization.Construct),
    };
  };

  export namespace Create {
    export type Props = {
      name?: string;
      email: string;
      profileImageSrc?: string;
    };
    export type Params = {
      operatorId?: Account.Id;
      idaasId?: IdaasId;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): Account => {
    const { operatorId, idaasId, props } = params;
    const id = Id.Generate();
    return {
      ...MutableEntity.Create({ operatorId: operatorId ?? id }),
      id,
      idaasId: Account.IdaasId.Construct(idaasId ?? { firebase: undefined }),
      type: 'user',
      name: props.name ?? normalizeEmail(props.email),
      email: normalizeEmail(props.email),
      profileImageSrc: props.profileImageSrc ?? '',
      isSystemAdmin: false,
      verificationEmailSentAt: undefined,
      memberships: [],
    };
  };

  export namespace Update {
    export type UpdatableKey = 'name' | 'profileImageSrc';
    export type UpdateProps = Pick<Account, UpdatableKey>;
    export type Params = {
      cur: Account;
      updateProps: UpdateProps;
    };
  }
  export const Update = (params: Update.Params): Account => {
    const { cur, updateProps } = params;
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId: cur.id }),
      ...updateProps,
    };
  };

  export namespace UpdateIsSystemAdmin {
    export type Params = {
      cur: Account;
      isSystemAdmin: Account['isSystemAdmin'];
    };
  }
  export const UpdateIsSystemAdmin = (params: UpdateIsSystemAdmin.Params): Account => {
    const { cur, isSystemAdmin } = params;

    if (!cur.email.includes(`@${Const.RtMailDomain}`)) {
      throw new Error('システム管理者に指定できるのは、社内アカウントのみです。');
    }

    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId: cur.id }),
      isSystemAdmin,
    };
  };

  export namespace AddMembershipIfNotExists {
    export type Props = {
      membership: Membership.Organization;
    };
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      props: Props;
    };
  }
  export const AddMembershipIfNotExists = (params: AddMembershipIfNotExists.Params): Account => {
    const {
      operatorId,
      account,
      props: { membership },
    } = params;

    const orgMap = new Map(account.memberships.map((m) => [m.organizationId, m]));
    const cur = orgMap.get(membership.organizationId);
    if (cur) {
      const projectMap = new Map(cur.projects.map((p) => [p.projectId, p]));
      for (const project of membership.projects) {
        if (projectMap.has(project.projectId)) continue;
        projectMap.set(project.projectId, project);
      }
      orgMap.set(membership.organizationId, { ...cur, projects: Array.from(projectMap.values()) });
    } else {
      orgMap.set(membership.organizationId, membership);
    }

    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships: Array.from(orgMap.values()),
    };
  };

  export namespace TouchVerificationEmailSentAt {
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      sentAt?: dayjs;
    };
  }
  export const TouchVerificationEmailSentAt = (params: TouchVerificationEmailSentAt.Params): Account => {
    const { operatorId, account, sentAt = dayjs(new Date()) } = params;
    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      verificationEmailSentAt: sentAt,
    };
  };

  export namespace CanResendVerificationEmail {
    export type Params = {
      account: Account;
      now?: dayjs;
    };
  }
  export const CanResendVerificationEmail = (params: CanResendVerificationEmail.Params): boolean => {
    const { account, now = dayjs(new Date()) } = params;
    return account.verificationEmailSentAt ? dayjs(account.verificationEmailSentAt).add(5, 'minute').isBefore(now) : true;
  };

  export namespace IsAuthSessionInvalidated {
    export type Params = {
      account: Pick<Account, 'authSessionInvalidBefore'>;
      authTime: dayjs | undefined;
    };
  }
  export const IsAuthSessionInvalidated = (params: IsAuthSessionInvalidated.Params): boolean => {
    const { account, authTime } = params;
    if (!account.authSessionInvalidBefore) return false;
    if (!authTime) return true;
    return !authTime.isAfter(account.authSessionInvalidBefore);
  };
  export namespace AssignOrganizationRole {
    export type Props = {
      organizationId: Organization.Id;
      roleId: Organization.Role.Id;
    };
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      props: Props;
    };
  }
  export const AssignOrganizationRole = (params: AssignOrganizationRole.Params): Account => {
    const { operatorId, account, props } = params;
    const orgMap = new Map(account.memberships.map((m) => [m.organizationId, m]));
    const cur = orgMap.get(props.organizationId);
    if (!cur) {
      throw new Error('組織に所属していません');
    }
    orgMap.set(props.organizationId, Membership.Organization.Assign({ cur, props: { roleId: props.roleId } }));
    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships: Array.from(orgMap.values()),
    };
  };

  export namespace SetSamlLoginEnforced {
    export type Props = {
      organizationId: Organization.Id;
      samlLoginEnforced: boolean;
    };
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      props: Props;
    };
  }
  export const SetSamlLoginEnforced = (params: SetSamlLoginEnforced.Params): Account => {
    const { operatorId, account, props } = params;
    const orgMap = new Map(account.memberships.map((m) => [m.organizationId, m]));
    const cur = orgMap.get(props.organizationId);
    if (!cur) {
      throw new Error('組織に所属していません');
    }
    orgMap.set(props.organizationId, Membership.Organization.Assign({ cur, props: { samlLoginEnforced: props.samlLoginEnforced } }));
    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships: Array.from(orgMap.values()),
    };
  };

  export namespace AssignPermissionGroup {
    export type Props = {
      projectId: Project.Id;
      groupId?: Group.Id | null | undefined;
    };
    export type Params = {
      operatorId: Account.Id;
      organizationId: Organization.Id;
      account: Account;
      props: Props;
    };
  }
  export const AssignPermissionGroup = (params: AssignPermissionGroup.Params): Account => {
    const { operatorId, organizationId, account, props } = params;

    const orgMap = new Map(account.memberships.map((m) => [m.organizationId, m]));
    const org = orgMap.get(organizationId);
    if (!org) {
      throw new Error('組織に所属していません');
    }

    const projectMap = new Map(org.projects.map((p) => [p.projectId, p]));
    const curProject = projectMap.get(props.projectId);
    if (!curProject) {
      throw new Error('プロジェクトに所属していません');
    }

    const nextProject: Membership.Project = { ...curProject, groupId: props.groupId ?? undefined };

    projectMap.set(props.projectId, nextProject);
    orgMap.set(organizationId, { ...org, projects: Array.from(projectMap.values()) });

    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships: Array.from(orgMap.values()),
    };
  };

  export const getOrganizationRoleId = (params: { account: Account; organizationId: Organization.Id }): Organization.Role.Id | undefined =>
    params.account.memberships.find((m) => m.organizationId === params.organizationId)?.roleId;

  export const isProjectMember = (params: { account: Account; projectId: Project.Id }): boolean =>
    params.account.memberships.some((membership) => membership.projects.some((project) => project.projectId === params.projectId));

  export const getDefaultProjectId = (account: Account): Project.Id | undefined => {
    return account.memberships[0]?.projects[0]?.projectId;
  };

  export const getMembershipOrganizationIds = (account: Account): Organization.Id[] => {
    return account.memberships.map((org) => org.organizationId);
  };

  export const getMembershipProjectIds = (account: Account): Project.Id[] => {
    return account.memberships.flatMap((org) => org.projects.map((p) => p.projectId));
  };

  export const IsSystemAdmin = (account: Account): boolean => account.isSystemAdmin;

  export const IsActive = (account: Account): boolean => account.type !== 'user' || !!account.idaasId.firebase;

  export namespace RemoveOrganizationMembership {
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      organizationId: Organization.Id;
    };
  }
  export const RemoveOrganizationMembership = (params: RemoveOrganizationMembership.Params): Account => {
    const { operatorId, account, organizationId } = params;

    const memberships = account.memberships.filter((m) => m.organizationId !== organizationId);

    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships,
    };
  };

  export namespace RemoveProjectMembership {
    export type Params = {
      operatorId: Account.Id;
      account: Account;
      projectId: Project.Id;
    };
  }
  export const RemoveProjectMembership = (params: RemoveProjectMembership.Params): Account => {
    const { operatorId, account, projectId } = params;

    const memberships = account.memberships.map((org) => {
      const projects = org.projects.filter((p) => p.projectId !== projectId);
      return { ...org, projects };
    });

    return {
      ...account,
      ...MutableEntity.Update({ cur: account, operatorId }),
      memberships,
    };
  };

  export namespace Is {
    export const Righttouch = (account: Pick<Account, 'email'>): boolean => account.email.endsWith(`@${Const.RtMailDomain}`);
  }

  export namespace DefaultRootAccount {
    export const Id = '6aUH6RfSY0' as Account.Id;
  }
}
